import React, { useState, useMemo } from "react";
import {
  Box,
  Table,
  TableBody,
  styled,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Paper,
  Typography,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Divider,
  Container,
  Checkbox,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Link from "@mui/material/Link";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import Section from "../../../../components/section/Section";
import { BodyHighlighter } from "../../../../components/fitness/BodyHighlighter.tsx";

const slugMap = {
  "Gluteus Medius": "gluteal",
  "Glute Maximus": "gluteal",
  "Lower Pecs": "chest",
  "Upper Pecs": "chest",
  "Front Delt": "deltoids",
  "Lateral Delt": "deltoids",
  "Rear Delt": "deltoids",
  "Lower Traps": "trapezius",
  "Middle Traps": "trapezius",
  "Upper Traps": "trapezius",
  Lats: "upper-back",
  Biceps: "biceps",
  "Vasti of the Quads": "quadriceps",
  "Rectus Femoris": "quadriceps",
  "Biceps Femoris Short Head": "hamstring",
  "Biceps Femoris Long Head & Semitendinosus/Semimembranosus": "hamstring",
  Gastrocnemius: "calves",
  Soleus: "calves",
  Abs: "abs",
};

function slugify(name) {
  return name
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const urlRegex = /(https?:\/\/[^\s]+)/g;

function renderWithLinks(text) {
  const parts = text.split(urlRegex);
  return parts.map((part, i) =>
    urlRegex.test(part) ? (
      <Link
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
      >
        {part}
      </Link>
    ) : (
      <span key={i}>{part}</span>
    )
  );
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: "#7A3B69",
  color: "#fff",
  fontWeight: "bold",
}));

// Helper function to group exercises by body part
const groupByBodyPart = (exercises) => {
  return exercises.reduce((groups, exercise) => {
    // Use either bodyPartName or fallback to bodyPart?.name
    const bodyPart =
      exercise.bodyPartName ||
      (exercise.bodyPart && exercise.bodyPart.name) ||
      "Unknown";
    if (!groups[bodyPart]) {
      groups[bodyPart] = [];
    }
    groups[bodyPart].push(exercise);
    return groups;
  }, {});
};

const ExerciseListing = ({ exercises, headerColor }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Update state for multiple selection filters (defaulting to "All")
  const [filterNames, setFilterNames] = useState(["All"]);
  const [filterMainFamilies, setFilterMainFamilies] = useState(["All"]);
  const [filterBodyParts, setFilterBodyParts] = useState(["All"]);

  // State for dialog popup to show technical fiche details
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [open, setOpen] = useState(false);

  const highlightData = useMemo(() => {
    if (!selectedExercise?.intensities) return [];
    return selectedExercise.intensities.map((i) => {
      const name = i.muscleGroupDetails?.name || String(i.muscleGroupId);
      // first try the map, then fallback to slugify
      const slug = slugMap[name] || slugify(name);
      return {
        slug,
        intensity: i.intensity,
        side: i.side,
      };
    });
  }, [selectedExercise]);

  // Compute unique values from the exercises list for each filter
  const uniqueNames = useMemo(() => {
    const names = exercises.map((e) => e.name).filter(Boolean);
    return ["All", ...Array.from(new Set(names)).sort()];
  }, [exercises]);

  const uniqueMainFamilies = useMemo(() => {
    const families = exercises
      .map((e) => e.mainFamilyName || (e.mainFamily && e.mainFamily.name))
      .filter(Boolean);
    return ["All", ...Array.from(new Set(families)).sort()];
  }, [exercises]);

  const uniqueBodyParts = useMemo(() => {
    const parts = exercises
      .map((e) => e.bodyPartName || (e.bodyPart && e.bodyPart.name))
      .filter(Boolean);
    return ["All", ...Array.from(new Set(parts)).sort()];
  }, [exercises]);

  // Helper to check if an exercise matches the selected filter(s)
  const matchesFilter = (selectedFilters, value) =>
    selectedFilters.length === 0 ||
    selectedFilters.includes("All") ||
    selectedFilters.includes(value);

  // Filter the exercises based on selected dropdown values
  const filteredExercises = useMemo(() => {
    return exercises.filter((e) => {
      const exerciseName = e.name;
      const exerciseFamily =
        e.mainFamilyName || (e.mainFamily && e.mainFamily.name);
      const exerciseBodyPart =
        e.bodyPartName || (e.bodyPart && e.bodyPart.name);
      return (
        matchesFilter(filterNames, exerciseName) &&
        matchesFilter(filterMainFamilies, exerciseFamily) &&
        matchesFilter(filterBodyParts, exerciseBodyPart)
      );
    });
  }, [exercises, filterNames, filterMainFamilies, filterBodyParts]);

  const groupedExercises = useMemo(() => {
    // 1) bucket into bodyPart → muscleGroup
    const nested = filteredExercises.reduce((acc, ex) => {
      const bodyPart =
        ex.bodyPartName || (ex.bodyPart && ex.bodyPart.name) || "Unknown";
      // pick the muscleGroup key you want—here I use the first intensity’s name
      const mainFamily =
        ex.mainFamilyName ||
        (ex.mainFamily && ex.mainFamily.name) ||
        "Unknown family name";

      if (!acc[bodyPart]) acc[bodyPart] = {};
      if (!acc[bodyPart][mainFamily]) acc[bodyPart][mainFamily] = [];
      acc[bodyPart][mainFamily].push(ex);
      return acc;
    }, {});

    // 2) sort bodyPart keys, then for each, sort muscleGroup keys
    return Object.keys(nested)
      .sort((a, b) => a.localeCompare(b))
      .reduce((out, bodyPart) => {
        out[bodyPart] = Object.keys(nested[bodyPart])
          .sort((a, b) => a.localeCompare(b))
          .reduce((mgOut, mainFamily) => {
            mgOut[mainFamily] = nested[bodyPart][mainFamily];
            return mgOut;
          }, {});
        return out;
      }, {});
  }, [filteredExercises]);

  // Open technical fiche popup when an item is clicked
  const handleItemClick = (exercise) => {
    setSelectedExercise(exercise);
    setOpen(true);
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" sx={{ textAlign: "center", mb: 2 }}>
        Exercises List
      </Typography>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 2,
          flexWrap: "wrap",
          mb: 3,
        }}
      >
        {/* Filter by Exercise Name */}
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={uniqueNames}
          value={filterNames}
          onChange={(event, newValue) => setFilterNames(newValue)}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              {option}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              label="Filter by Exercise"
              placeholder="Select exercise(s)"
            />
          )}
          sx={{ minWidth: { xs: 150, sm: 200 } }}
        />

        {/* Filter by Main Family */}
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={uniqueMainFamilies}
          value={filterMainFamilies}
          onChange={(event, newValue) => setFilterMainFamilies(newValue)}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              {option}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              label="Filter by Main Family"
              placeholder="Select main family/families"
            />
          )}
          sx={{ minWidth: { xs: 150, sm: 200 } }}
        />

        {/* Filter by Body Part */}
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={uniqueBodyParts}
          value={filterBodyParts}
          onChange={(event, newValue) => setFilterBodyParts(newValue)}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              {option}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              label="Filter by Body Part"
              placeholder="Select body part(s)"
            />
          )}
          sx={{ minWidth: { xs: 150, sm: 200 } }}
        />
      </Box>

      <Container sx={{ py: 4, px: { xs: 2, md: 4 } }}>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Box sx={{ width: "100%", px: 2 }}>
            {isMobile ? (
              // Render a simple list for mobile devices
              <List>
                {Object.entries(groupedExercises).map(
                  ([bodyPart, families]) => (
                    <Box key={bodyPart} sx={{ mb: 2 }}>
                      {/* BODY PART HEADER */}
                      <ListItem
                        sx={{
                          backgroundColor: "#7A3B69",
                          color: theme.palette.primary.contrastText,
                        }}
                      >
                        <ListItemText
                          primary={bodyPart}
                          primaryTypographyProps={{
                            variant: "subtitle1",
                            fontWeight: "bold",
                          }}
                        />
                      </ListItem>

                      {/* FOR EACH MAIN FAMILY UNDER THAT BODY PART */}
                      {Object.entries(families).map(
                        ([mainFamily, exercises]) => (
                          <React.Fragment key={mainFamily}>
                            {/* Main‐Family Subheader */}
                            <ListItem
                              sx={{ pl: 4, backgroundColor: "#F2E9DC" }}
                            >
                              <ListItemText
                                primary={mainFamily}
                                primaryTypographyProps={{ fontStyle: "italic" }}
                              />
                            </ListItem>
                            {/* The actual exercises */}
                            {exercises.map((exercise) => (
                              <ListItem
                                key={exercise.id}
                                button
                                onClick={() => handleItemClick(exercise)}
                                sx={{ pl: 8, borderBottom: "1px solid #ddd" }}
                              >
                                <ListItemText primary={exercise.name} />
                              </ListItem>
                            ))}
                          </React.Fragment>
                        )
                      )}
                    </Box>
                  )
                )}
              </List>
            ) : (
              // Desktop view with the full table
              <TableContainer
                component={Paper}
                sx={{
                  maxHeight: 800, // or any fixed height to enable scrolling
                  borderRadius: 2,
                  overflowX: "auto",
                  boxShadow: 3,
                }}
              >
                <Table stickyHeader>
                  <TableHead sx={{ backgroundColor: "#7A3B69" }}>
                    <TableRow
                      variant="h5"
                      sx={{
                        backgroundColor: "#7A3B69",
                        color: "#fff",
                        p: 1,
                        borderRadius: 1,
                      }}
                    >
                      <StyledTableCell>
                        <Typography variant="h6">Name</Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Typography variant="h6">French Name</Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Typography variant="h6">Main Family</Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Typography variant="h6">Body Part</Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Typography variant="h6">Muscle Groups</Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Typography variant="h6">Execution</Typography>
                      </StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(groupedExercises).map(
                      ([bodyPart, mainFamily]) => (
                        <React.Fragment key={bodyPart}>
                          {/* 1) Body-part header */}
                          <TableRow>
                            <TableCell
                              colSpan={6}
                              sx={{
                                backgroundColor: theme.palette.primary.light,
                                color: theme.palette.primary.contrastText,
                                fontWeight: "bold",
                              }}
                            >
                              {bodyPart}
                            </TableCell>
                          </TableRow>

                          {/* 2) For each muscleGroup under this bodyPart */}
                          {Object.entries(mainFamily).map(
                            ([mainFamily, exercises]) => (
                              <React.Fragment key={mainFamily}>
                                {/* Muscle-group sub-header */}
                                <TableRow>
                                  <TableCell
                                    colSpan={6}
                                    sx={{
                                      pl: 4,
                                      fontStyle: "italic",
                                      backgroundColor: "#F2E9DC",
                                    }}
                                  >
                                    {mainFamily}
                                  </TableCell>
                                </TableRow>
                                {/* 3) Each exercise row */}
                                {exercises.map((exercise) => (
                                  <TableRow
                                    key={exercise.id}
                                    onClick={() => handleItemClick(exercise)}
                                    sx={{
                                      "&:hover": {
                                        backgroundColor:
                                          theme.palette.action.hover,
                                        cursor: "pointer",
                                      },
                                    }}
                                  >
                                    <TableCell>{exercise.name}</TableCell>
                                    <TableCell>
                                      {exercise.frenchName ||
                                        exercise.frenchName?.name}
                                    </TableCell>
                                    <TableCell>
                                      {exercise.mainFamilyName ||
                                        exercise.mainFamily?.name}
                                    </TableCell>
                                    <TableCell>
                                      {exercise.bodyPartName ||
                                        exercise.bodyPart?.name}
                                    </TableCell>
                                    <TableCell>
                                      {(
                                        exercise.intensities?.map(
                                          (i) =>
                                            i.muscleGroupDetails?.name ||
                                            i.muscleGroupId
                                        ) || []
                                      ).join(", ")}
                                    </TableCell>
                                    <TableCell>{exercise.execution}</TableCell>
                                  </TableRow>
                                ))}
                              </React.Fragment>
                            )
                          )}
                        </React.Fragment>
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Box>
      </Container>

      {/* Technical Fiche Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{selectedExercise?.name}</DialogTitle>
        <DialogContent dividers>
          {/* Exercise Overview */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Exercise Overview
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <Grid container spacing={2}>
              {[
                [
                  "Nom français",
                  selectedExercise?.frenchName ||
                    selectedExercise?.frenchName?.name,
                ],
                [
                  "Main Family",
                  selectedExercise?.mainFamilyName ||
                    selectedExercise?.mainFamily?.name,
                ],
                [
                  "Body Part",
                  selectedExercise?.bodyPartName ||
                    selectedExercise?.bodyPart?.name,
                ],
                ["Execution Style", selectedExercise?.execution],
                ["Tags", selectedExercise?.tagNames?.join(", ")],
              ].map(([label, value]) => (
                <Grid item xs={6} key={label}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: "bold",
                      color: "text.secondary",
                      mb: 0.5,
                    }}
                  >
                    {label}
                  </Typography>
                  <Typography variant="body1">{value || "—"}</Typography>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Target Muscles (Front & Back)
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <Grid container spacing={2} justifyContent="center">
              {/* Front view */}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" align="center">
                  Front
                </Typography>
                <BodyHighlighter
                  side="front"
                  scale={0.4}
                  data={highlightData}
                  onPartClick={(part, whichSide) => {
                    console.log("clicked front", part, whichSide);
                  }}
                />
              </Grid>

              {/* Back view */}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" align="center">
                  Back
                </Typography>
                <BodyHighlighter
                  side="back"
                  scale={0.4}
                  data={highlightData}
                  onPartClick={(part, whichSide) => {
                    console.log("clicked back", part, whichSide);
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Description */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Exercise Description
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <Typography variant="body2">
              {selectedExercise?.description}
            </Typography>
          </Box>

          {/* Video */}
          {selectedExercise?.videoUrl && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Instructional Video
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="body2">
                <a
                  href={selectedExercise.videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {selectedExercise.videoUrl}
                </a>
              </Typography>
            </Box>
          )}

          {/* Notes */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Important Notes
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <Typography variant="body2">{selectedExercise?.notes}</Typography>
          </Box>

          {/* Steps */}
          {selectedExercise?.steps && selectedExercise.steps.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Exercise Steps
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <ol>
                {selectedExercise.steps.map((step) => (
                  <li key={step.id}>
                    <Typography variant="body2">
                      {renderWithLinks(step.description)}
                    </Typography>
                  </li>
                ))}
              </ol>
            </Box>
          )}

          {/* Equipment */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Required Equipment
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <Typography variant="body2">
              {selectedExercise?.equipmentNames &&
              selectedExercise.equipmentNames.length > 0
                ? selectedExercise.equipmentNames.join(", ")
                : "None"}
            </Typography>
          </Box>

          {/* Muscle Intensity Breakdown */}
          {selectedExercise?.intensities?.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Muscle Groups
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="body2">
                {selectedExercise.intensities
                  .map((i) => i.muscleGroupDetails?.name || i.muscleGroupId)
                  .join(", ")}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExerciseListing;
